import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";
import { IconBtn, portalRoot } from "./primitives";

export interface ModalProps {
  open: boolean;
  onClose?: () => void;
  children: ReactNode;
  width?: number;
  title?: string;
  /** Name aus der Icon-Registry — erscheint links vom Titel, auf dem Handy ausgeblendet */
  icon?: string;
  subtitle?: string;
  footer?: ReactNode;
  closeOnBackdrop?: boolean;
  /** Aktionen in der Kopfzeile links vom X (z. B. Modus-Umschalter) */
  headerExtra?: ReactNode;
}

export function Modal({ open, onClose, children, width = 520, title, icon, subtitle, footer, closeOnBackdrop = true, headerExtra }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div
      className="anim-fade modal-wrap"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      <div
        onClick={() => closeOnBackdrop && onClose && onClose()}
        style={{ position: "absolute", inset: 0, background: "rgba(8,8,8,0.5)", backdropFilter: "blur(4px)" }}
      />
      <div
        className="glass-strong anim-pop scroll modal-card"
        // dvh statt vh: mobile Browserleisten schrumpfen den sichtbaren Bereich —
        // mit 90vh ragte das zentrierte Modal oben UND unten aus dem Schirm
        style={{ position: "relative", width, maxWidth: "100%", maxHeight: "min(90vh, calc(100dvh - 32px))", overflowY: "auto", borderRadius: 22, boxShadow: "var(--shadow-lg)", ...(window.matchMedia("(max-width: 640px)").matches ? { background: "var(--panel-solid)", backdropFilter: "none" } : {}) }}
      >
        {title && (
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, padding: "22px 24px 16px" }}>
            <div style={{ display: "flex", gap: 13 }}>
              {icon && (
                <div
                  className="mobile-hide"
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 11,
                    background: "var(--accent-soft)",
                    color: "var(--accent-text)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "none",
                  }}
                >
                  <Icon name={icon} size={20} />
                </div>
              )}
              <div>
                <h3 style={{ fontSize: 19 }}>{title}</h3>
                {subtitle && <p style={{ fontSize: 13.5, color: "var(--text-mute)", marginTop: 3 }}>{subtitle}</p>}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flex: "none" }}>
              {headerExtra}
              <IconBtn icon="x" size={18} box={34} onClick={onClose} title="Schließen" />
            </div>
          </div>
        )}
        <div style={{ padding: title ? "0 24px 22px" : 24 }}>{children}</div>
        {footer && (
          <div className="modal-foot" style={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 10, padding: "16px 24px", borderTop: "1px solid var(--glass-border)" }}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    portalRoot(),
  );
}
